import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import { fetchCompanySelfAssessmentDashboard } from './companySelfAssessmentApi';
import OutcomeMixPanel from './OutcomeMixPanel';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  BigStatPanel,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID,
  CHART_GRID_NO_X
} from '../../../../component/common/designSystem';
import {
  SCORE_RAMP,
  ATTACHMENT_RAMP,
  VOLUME_RAMP,
  DURATION_RAMP,
  FUNNEL_RAMP
} from './selfAssessmentTheme';

/**
 * Self-Assessment → Dashboard.
 *
 * Three things carry the page:
 *
 *  1. The START → FINISH FUNNEL. A run is written the moment the first question
 *     is answered, so an unfinished run is a real abandonment rather than an
 *     inference. A company where half the runs never finish has a very
 *     different problem from one where nobody starts.
 *  2. The PER-ASSESSMENT OUTCOME MIX, which is the reason this section is worth
 *     building. Each bar is one questionnaire normalised to its own total, so a
 *     rarely-taken assessment is comparable to a popular one and "most people
 *     land in the bottom quartile of this one" becomes visible.
 *  3. THE TWO KINDS OF OUTCOME, kept apart. Scored assessments return a
 *     percentage; attachment assessments return a style and have no score. They
 *     are never summed into one number.
 *
 * The company-wide quartile spread is shown as context for the volume, not as a
 * wellbeing score — direction belongs to the individual questionnaire, and 80%
 * on a resilience assessment means the opposite of 80% on a burnout one.
 *
 * `fetcher` is a prop so the super-admin Self-Assessment hub can render this
 * page unscoped; the builders behind both are the same.
 */
const SelfAssessmentDashboard = ({
  fetcher = fetchCompanySelfAssessmentDashboard,
  populationLabel = 'users'
}) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  const scoreBands = stats?.scoreBands || [];
  const volumeSplit = stats?.volumeSplit || [];
  const durationSplit = stats?.durationSplit || [];
  const weekdayCompletions = stats?.weekdayCompletions || [];
  const byAssessment = stats?.byAssessment || [];
  const completionsTrend = stats?.completionsTrend || [];
  const peopleTrend = stats?.peopleTrend || [];

  // The two families never share an axis, so they get a panel each.
  const scoredAssessments = byAssessment.filter((row) => row.kind === 'score');
  const attachmentAssessments = byAssessment.filter((row) => row.kind === 'attachment');

  // What share of the population has ever finished an assessment.
  const reachPercentage =
    stats?.totalUsers > 0 ? Math.round((stats.usersCompleted / stats.totalUsers) * 100) : null;

  const trendHasData = completionsTrend.some((val) => val > 0);

  /**
   * Thirty days of completions, with the number of distinct people as a line on
   * top. The gap between the two is the repeat behaviour: bars well above the
   * line mean a handful of people working through the catalogue, not broad
   * take-up.
   */
  const trendOptions = {
    chart: { ...CHART_BASE, type: 'line', stacked: false },
    colors: [CHART_COLORS.brand, CHART_COLORS.teal],
    stroke: { width: [0, 3], curve: 'smooth' },
    plotOptions: { bar: { columnWidth: '65%', borderRadius: 2 } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(completionsTrend.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { shared: true }
  };

  /**
   * One bar, split three ways. A funnel with three states reads better stacked
   * than as three separate cards, because the whole question is what share of
   * the starts each state took.
   */
  const funnelOptions = {
    chart: { ...CHART_BASE, type: 'bar', stacked: true, stackType: '100%' },
    colors: FUNNEL_RAMP,
    plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: '45%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID_NO_X,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: ['Runs started'], labels: { formatter: (val) => `${Math.round(val)}%` } },
    tooltip: { shared: true, y: { formatter: (val) => `${val.toLocaleString()} runs` } }
  };

  const weekdayOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.teal],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: weekdayCompletions.map((day) => day.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} completions` } }
  };

  return (
    <div>
      {loader ? <Loader /> : null}

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title="People Taking Them"
          value={stats ? formatBigNumber(stats.usersCompleted) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Assessments Completed"
          value={stats ? formatBigNumber(stats.totalCompletions) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.score}
          title="Completion Rate"
          value={stats?.runsStarted > 0 ? `${stats.completionRate}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.catalog}
          title="Assessments Used"
          value={stats ? `${stats.assessmentsUsed} of ${stats.totalAssessments}` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.streak}
          title="Came Back for More"
          value={stats ? formatBigNumber(stats.repeatUsers) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.logs}
          title="Per Person"
          value={stats ? stats.avgPerUser : '-'}
        />
      </div>

      {/* ---------- Spread, volume, effort ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {/* Quartiles across every scored assessment at once. Useful as a shape —
            a company piled into one quartile is worth a second look — but not a
            wellbeing score, since direction differs per questionnaire. */}
        <MiniDonutCard
          title="Score Spread"
          subtitle="Scored assessments, by quartile"
          data={scoreBands}
          colors={SCORE_RAMP}
          unit="completions"
          totalLabel="Scored"
        />
        <MiniDonutCard
          title="How Many They Take"
          subtitle="Assessments completed per person"
          data={volumeSplit}
          colors={VOLUME_RAMP}
          unit="people"
          totalLabel="People"
        />
        <MiniDonutCard
          title="Time to Complete"
          subtitle="First question to last"
          data={durationSplit}
          colors={DURATION_RAMP}
          unit="completions"
          totalLabel="Completions"
          legendLimit={4}
        />
      </div>

      {/* ---------- Trend and funnel ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Completions Over Time" subtitle="Assessments finished — last 30 days">
          {trendHasData ? (
            <Chart
              type="line"
              height={380}
              width="100%"
              series={[
                { name: 'Completions', type: 'column', data: completionsTrend },
                { name: 'People', type: 'line', data: peopleTrend }
              ]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>No assessments completed in the last 30 days.</PanelEmpty>
          )}
        </Panel>

        {/* The funnel only this section can measure: a run is written on the
            first answer, so an unfinished one is a real drop-off. */}
        <Panel title="Start to Finish" subtitle="What happens once someone begins an assessment">
          {stats?.runsStarted > 0 ? (
            <>
              <Chart
                type="bar"
                height={180}
                width="100%"
                series={[
                  { name: 'Finished', data: [stats.runsCompleted] },
                  { name: 'Still open', data: [stats.runsInProgress] },
                  { name: 'Abandoned', data: [stats.runsLeft] }
                ]}
                options={funnelOptions}
              />
              <PanelTable
                head={
                  <>
                    <th className="py-2 pr-4 font-medium">Stage</th>
                    <th className="py-2 font-medium text-right">Runs</th>
                  </>
                }
              >
                <tr className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">Started</td>
                  <td className="py-2 text-right">{formatBigNumber(stats.runsStarted)}</td>
                </tr>
                <tr className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">Finished</td>
                  <td className="py-2 text-right">{formatBigNumber(stats.runsCompleted)}</td>
                </tr>
                <tr className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">Still open</td>
                  <td className="py-2 text-right">{formatBigNumber(stats.runsInProgress)}</td>
                </tr>
                <tr className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">Abandoned</td>
                  <td className="py-2 text-right">{formatBigNumber(stats.runsLeft)}</td>
                </tr>
              </PanelTable>
            </>
          ) : (
            <PanelEmpty>Nobody has started an assessment yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Take-up per assessment ---------- */}
      <div className="mt-6">
        <Panel
          title="Assessment Take-up"
          subtitle={`Which questionnaires your ${populationLabel} actually finish`}
        >
          {byAssessment.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Assessment</th>
                  <th className="py-2 px-4 font-medium text-center">Completed</th>
                  <th className="py-2 px-4 font-medium text-center">People</th>
                  <th className="py-2 px-4 font-medium text-center">Finish Rate</th>
                  <th className="py-2 px-4 font-medium text-center">Avg Score</th>
                  <th className="py-2 font-medium text-right">Last Taken</th>
                </>
              }
            >
              {byAssessment.map((row) => (
                <tr key={String(row._id)} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{row.title}</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.completions)}</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.users)}</td>
                  <td className="py-2 px-4 text-center">{row.completionRate}%</td>
                  {/* Attachment assessments have no score at all, so a dash
                      rather than a zero — the difference matters. */}
                  <td className="py-2 px-4 text-center">
                    {row.avgScore != null ? `${row.avgScore}%` : '-'}
                  </td>
                  <td className="py-2 text-right">{row.lastDate || '-'}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No assessments completed yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Where people land ----------
          The panels the section exists for. Each row is one questionnaire
          normalised to its own total, so an assessment taken twelve times can
          be read next to one taken two hundred times. */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <OutcomeMixPanel
          title="Where People Land"
          subtitle="Scored assessments, by quartile within each one"
          rows={scoredAssessments}
          colors={SCORE_RAMP}
          emptyText="No scored assessments completed yet."
        />
        <OutcomeMixPanel
          title="Attachment Styles"
          subtitle="Attachment assessments, by style within each one"
          rows={attachmentAssessments}
          colors={ATTACHMENT_RAMP}
          emptyText="No attachment assessments completed yet."
        />
      </div>

      {/* ---------- Reach and supporting detail ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <BigStatPanel
          title="Self-Assessment Reach"
          subtitle={`Share of ${populationLabel} who have finished one`}
          value={reachPercentage != null ? `${reachPercentage}%` : '-'}
          caption={
            stats
              ? `${formatBigNumber(stats.usersCompleted)} of ${formatBigNumber(
                  stats.totalUsers
                )} ${populationLabel}`
              : null
          }
        />

        <Panel title="Completions by Weekday" subtitle="When people sit down with one">
          {weekdayCompletions.some((day) => day.count > 0) ? (
            <Chart
              type="bar"
              height={280}
              width="100%"
              series={[{ name: 'Completions', data: weekdayCompletions.map((day) => day.count) }]}
              options={weekdayOptions}
            />
          ) : (
            <PanelEmpty>No assessments completed yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Assessment Summary" subtitle="The raw counts behind this page">
          <PanelTable
            head={
              <>
                <th className="py-2 pr-4 font-medium">Measure</th>
                <th className="py-2 font-medium text-right">Value</th>
              </>
            }
          >
            {/* Started but never finished is the gap between these two. */}
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">People who started one</td>
              <td className="py-2 text-right">
                {stats ? formatBigNumber(stats.usersStarted) : '-'}
              </td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">People who finished one</td>
              <td className="py-2 text-right">
                {stats ? formatBigNumber(stats.usersCompleted) : '-'}
              </td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Scored completions</td>
              <td className="py-2 text-right">
                {stats ? formatBigNumber(stats.scoredCompletions) : '-'}
              </td>
            </tr>
            {/* Averaged across every scored questionnaire, so it is a shape
                check rather than a grade — see the panel note above. */}
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Average score</td>
              <td className="py-2 text-right">
                {stats?.avgScore != null ? `${stats.avgScore}%` : '-'}
              </td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">First completion</td>
              <td className="py-2 text-right">{stats?.firstCompletionDate || '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Last completion</td>
              <td className="py-2 text-right">{stats?.lastCompletionDate || '-'}</td>
            </tr>
          </PanelTable>
        </Panel>
      </div>
    </div>
  );
};

SelfAssessmentDashboard.propTypes = {
  fetcher: PropTypes.func,
  populationLabel: PropTypes.string
};

export default SelfAssessmentDashboard;
